import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, DoCheck, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { defer, forkJoin, from, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, filter as where, finalize, first, map, mergeMap, repeat, retry, takeUntil, takeWhile, toArray } from 'rxjs/operators';
import { DrawingIssueSet, DrawingSetRevision } from '../../../../../../../../shared/models/drawing-sheet';
import { ProjectDrawingsService } from '../../../../../../../../shared/services/project-drawings.service';
import { parseStationInput, stationInputText } from '../../../../../../../../shared/helpers/design-context';
import { SHEET_TYPES, SheetType, sheetTypeLabel } from '../../../../../../../../shared/helpers/sheet-types';
import { sheetPreviewCrop, SheetPreviewCrop } from '../../../../../../../../shared/helpers/sheet-preview-crop';
import { ProjectsService } from '../../../../../../core/projects.service';
import { DrawingSetPagePanelComponent, DrawingSetPagePanelData, PageSaveState, SetPageHost } from '../drawing-set-page-panel/drawing-set-page-panel.component';
import { DrawingsAnalytics, errorProps } from '../drawings-analytics';
import { DrawingPublishDialogComponent, DrawingPublishDialogData, DrawingPublishDialogResult } from '../drawing-publish-dialog/drawing-publish-dialog.component';
import { DrawingSetPageViewerComponent } from '../drawing-set-page-viewer/drawing-set-page-viewer.component';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { confirmAndDiscardSet } from '../drawing-withdraw';

const POLL_MS = 3000;
/** Consecutive failed polls before the review stops asking and says so (backoff 3, 6, 12, 24 s in between). */
const MAX_POLL_FAILURES = 5;
/** Cells a publisher types into: a poll leaves these alone on the row being edited */
const TYPED_FIELDS = ['number', 'title', 'label', 'discipline', 'sheet_type', 'station_start', 'station_end'] as const;
/** Bulk edit: PUTs in flight at once */
const BULK_CONCURRENCY = 4;
/**
 * The row's title-block crop: centred near the lower-right corner (where plan-set title blocks put the sheet title and
 * number), zoomed this many times the box width. Checked against the B-4 and TxDOT title blocks.
 */
const TITLE_BLOCK_CROP_ZOOM = 4;
const TITLE_BLOCK_CROP_POINT = { x: 0.9, y: 0.9 };
const TITLE_BLOCK_BOX_ASPECT = 120 / 48;
/** Until the page image loads: an 11x17 / ANSI B sheet */
const DEFAULT_PAGE_ASPECT = 17 / 11;
/** How the panel's save line names a field (W46 item 7) */
/** W48: the field a page save is reported as (stations as one; the sheet pick as match) */
const TRACKED_FIELDS: Record<string, string> = {
  number: 'number', title: 'title', discipline: 'discipline', sheet_type: 'sheet_type', label: 'label', station_start: 'stations',
  station_end: 'stations', sheetUuid: 'match',
};
const FIELD_NAMES: Record<string, string> = {
  number: 'sheet number', title: 'title', discipline: 'discipline', sheet_type: 'type', station_start: 'station', station_end: 'station',
  label: 'revision', sheetUuid: 'match', skip_publish: 'publish setting',
};

/** The number a duplicate is judged on: trimmed, case-blind; '' for none */
function numberKey(row: DrawingSetRevision): string {
  return row.number?.trim().toUpperCase() ?? '';
}

interface ProblemIndex {
  rows: DrawingSetRevision[];
  publishRows: DrawingSetRevision[];
  byNumber: Map<string, DrawingSetRevision[]>;
  problems: Set<DrawingSetRevision>;
}

/** Review M4: one pass over the set: the publishing pages by number, and the pages that block the publish */
function buildProblemIndex(rows: DrawingSetRevision[]): ProblemIndex {
  const publishRows = rows.filter(r => !r.skip_publish);
  const byNumber = new Map<string, DrawingSetRevision[]>();
  for (const r of publishRows) {
    const key = numberKey(r);
    if (key) {
      byNumber.set(key, [...(byNumber.get(key) ?? []), r]);
    }
  }
  const problems = new Set(publishRows.filter(r => {
    const key = numberKey(r);
    return !key || r.match?.kind === 'ambiguous' || (byNumber.get(key)?.length ?? 0) > 1;
  }));
  return { rows, publishRows, byNumber, problems };
}

export interface BulkFailure {
  row: DrawingSetRevision;
  message: string;
}
@Component({
  selector: 'app-drawing-set-review',
  templateUrl: './drawing-set-review.component.html',
  styleUrls: ['./drawing-set-review.component.scss'],
  standalone: false,
})
export class DrawingSetReviewComponent implements OnInit, DoCheck, OnDestroy, SetPageHost {
  /** The app's toast (Figma 11893:79195): CommonService.notification, no action button */
  private readonly notify = inject(CommonService);
  @Input() projectId: string;
  @Input() setUuid: string;
  @Input() canConfigure = false;
  @Input() canPublish = false;
  @Output() back = new EventEmitter<void>();
  @Output() published = new EventEmitter<DrawingIssueSet>();
  /** Opens carry-forward for the sheets this set superseded */
  @Output() reviewCarry = new EventEmitter<string>();
  /** Preview changes: compare the issued revision a page replaces (against) with this draft page (revision) */
  @Output() previewDiff = new EventEmitter<{ revision: string; against: string }>();
  /**
   * The project has no published sheets yet (the first issue): every page is a new sheet, so the Match column goes and
   * only the exceptions show. null until known.
   */
  firstIssue: boolean | null = null;
  set: DrawingIssueSet | null = null;
  publishing = false;
  /** After a publish: shared redlines on the superseded sheets waiting for a carry-forward decision */
  carryCount = 0;
  /** Read-only rows (W42): every field edits in the page side panel; the row opens it */
  readonly draftColumns = ['select', 'page', 'titleBlock', 'number', 'title', 'type', 'stations', 'match', 'actions'];
  /** A published or withdrawn set is a record: matching, bulk edit and dropping no longer apply */
  readonly issuedColumns = ['page', 'titleBlock', 'number', 'title', 'type', 'stations'];
  readonly stationText = stationInputText;
  readonly sheetTypes = SHEET_TYPES;
  readonly typeLabel = sheetTypeLabel;
  /** Pages per list or grid page; client-side (the set GET returns every revision) */
  readonly pageSize = 20;
  pageIndex = 0;
  viewMode: 'list' | 'grid' = 'list';
  /** "Needs attention" shows the pages that needed it when chosen; a page fixed meanwhile stays until it is chosen again */
  filter: 'all' | 'attention' = 'all';
  private attentionUuids = new Set<string>();
  /** The page the side panel shows: highlighted in the list */
  selectedUuid: string | null = null;
  /** Sri: the page under review (a row opens it; a one-page replacement opens on it), and the revision it replaces */
  reviewUuid: string | null = null;
  reviewAgainst: string | null = null;
  private autoReviewed = false;
  private readonly againstCache = new Map<string, string | null>();
  /** Bulk edit (W43): the checked pages, the bar's values ('' leaves a field as it is) and the outcome */
  readonly checked = new Set<string>();
  bulkDiscipline = '';
  bulkType = '';
  bulkLabel = '';
  /** '' leaves it as it is; 'skip' is "Skip this page" */
  bulkPublish: '' | 'publish' | 'skip' = '';
  bulkBusy = false;
  bulkDone = 0;
  bulkFailures: BulkFailure[] = [];
  private readonly dialog = inject(MatDialog);
  private readonly projects = inject(ProjectsService);
  private panel: DrawingSetPagePanelComponent | null = null;
  /** Asked for and not up yet: the right-panel host creates it 300 ms after the request (I1) */
  private panelRequested = false;
  private pendingPanel: DrawingSetPagePanelData | null = null;
  private destroyed = false;
  /** The row focus goes back to when the panel closes (I2) */
  private focusReturnUuid: string | null = null;
  /** Enter in the panel's Number field on a page to fix: moves on to the next one once its save lands (W46) */
  private advanceUuid: string | null = null;
  /** Item 7: each page's saves in flight and the panel's save line; `saveSettled` fires when a page has none left */
  /** Page edits (panel, ⋯ menu) still on their way: Publish waits for them (review 2 #2) */
  private pageEditsInFlight = 0;
  private readonly saves = new Map<string, { inFlight: number; fields: Set<string>; failedField: string | null; errorText: string; state: PageSaveState }>();
  private readonly saveSettled = new Subject<string>();
  /** W48: PostHog events; the review's open time for the publish's review_duration_s */
  private readonly track = inject(DrawingsAnalytics);
  private openedAt: number | null = null;
  private problemIndex: ProblemIndex | null = null;
  private problemIndexStale = true;
  /** The anchor of a shift-click range (item 8) */
  private lastChecked: string | null = null;
  /** Shift was held on the checkbox press: its change then selects a range */
  private shiftHeld = false;
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  /** Page image aspect (width / height) per revision, once its image has loaded */
  private readonly pageAspect = new Map<string, number>();
  private readonly crops = new Map<string, { aspect: number; crop: SheetPreviewCrop }>();
  /** Set when the inspect poll gave up after repeated failures */
  pollError = '';
  /** The row whose cell has focus: a poll leaves its fields alone */
  private editingUuid: string | null = null;
  private poll: Subscription | null = null;
  private readonly onDestroy = new Subject<void>();

  constructor(private api: ProjectDrawingsService, private cdr: ChangeDetectorRef) {}

  /**
   * Review M4: the problem index is rebuilt at most once per change-detection pass (and after this review's own edits),
   * not per row per binding. A row edited outside the review (a spec, a poll merge) is seen on the next pass.
   */
  ngDoCheck(): void {
    this.problemIndexStale = true;
  }

  ngOnInit(): void {
    this.startPoll();
    // One row of the register tells whether anything is published yet (the first issue)
    this.api.listSheets(this.projectId, { start: 0, total: 1 }).pipe(takeUntil(this.onDestroy)).subscribe({
      next: page => {
        this.firstIssue = (page?.totalRecords ?? 0) === 0;
        this.cdr.markForCheck();
      },
      error: () => (this.firstIssue = false),
    });
    // The record side panel's own close (its X, or another record opening) ends the selection
    this.projects.closeRightPanel.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.selectedUuid = null;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    // A panel still on its way closes too: attachPanel closes it the moment it comes up
    if (this.panel || this.panelRequested) {
      this.projects.closeRightPanel.next();
    }
    this.poll?.unsubscribe();
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  /** Rows can be edited only while the set is a draft */
  get editable(): boolean {
    return this.canConfigure && this.set?.status === 'draft';
  }

  get displayedColumns(): string[] {
    return !this.set || this.set.status === 'draft' ? this.draftColumns : this.issuedColumns;
  }

  /** The first issue's Match column is only the exceptions (No sheet number, Skipped, ...): no header, no "New sheet" chips */
  get exceptionsOnly(): boolean {
    return !!this.firstIssue && this.set?.status === 'draft';
  }

  /** "First issue: 528 new sheets · 1 skipped" */
  get firstIssueSummary(): string {
    if (!this.exceptionsOnly) return '';
    const included = this.publishRows.length;
    const skipped = (this.set?.revisions ?? []).filter(r => this.isSkipped(r)).length;
    return [`First issue: ${included} new ${included === 1 ? 'sheet' : 'sheets'}`, skipped ? `${skipped} skipped` : ''].filter(Boolean).join(' · ');
  }

  /** The header's title: a set made by Upload new revision names its target ("5 · PROJECT LAYOUT, new revision (replaces Rev A)") */
  get reviewTitle(): string {
    const t = this.set?.target_sheet;
    if (t && this.set.status === 'draft') {
      const name = [t.number, t.title].filter(Boolean).join(' · ');
      return `${name}, new revision${t.current_label ? ` (replaces Rev ${t.current_label})` : ''}`;
    }
    return this.set?.name || 'Drawing set';
  }

  /**
   * Wrong-drawing guard (api 7c9c36c0): "Title block says 5, not 6", "Looks different from Rev A". On a draft's
   * publishing pages only; null when there are none (the template's @if).
   */
  warningTexts(row: DrawingSetRevision): string[] | null {
    if (this.set?.status !== 'draft' || this.isSkipped(row)) {
      return null;
    }
    const out: string[] = [];
    if (row.number_mismatch) {
      out.push(`Title block says ${row.number_mismatch.read}, not ${row.number_mismatch.target}`);
    }
    if (row.similarity?.low) {
      out.push(`Looks different from ${row.similarity.against_label ? 'Rev ' + row.similarity.against_label : 'the current revision'}`);
    }
    return out.length ? out : null;
  }

  /** Preview changes vs the issued revision a page replaces: a draft page with a replaces match, not skipped */
  canPreview(row: DrawingSetRevision): boolean {
    return this.set?.status === 'draft' && row.match?.kind === 'replaces' && !!row.match.sheetUuid && !this.isSkipped(row);
  }

  previewLabel(row: DrawingSetRevision): string {
    return `Preview changes vs Rev ${row.match.currentLabel ?? ''}`.trim();
  }

  /** The issued revision is the replaced sheet's current one: read it, then compare it with this draft page */
  previewChanges(row: DrawingSetRevision, event?: Event): void {
    event?.stopPropagation();
    if (!this.canPreview(row)) return;
    this.api.getSheet(this.projectId, row.match.sheetUuid).pipe(takeUntil(this.onDestroy)).subscribe({
      next: sheet => {
        const against = sheet?.current?.uuid;
        if (against) this.previewDiff.emit({ revision: row.uuid, against });
      },
      error: e => this.notify.notification(this.api.requestFailure(e), 'error'),
    });
  }

  get inspecting(): boolean {
    return this.isInspecting(this.set);
  }

  /** The header's status badge: Processing while inspecting, then Ready to publish, Published (or why not) */
  get setStatusChip(): { label: string; variant: 'info' | 'warning' | 'success' | 'error' | 'neutral' } | null {
    const set = this.set;
    if (!set) return null;
    if (set.status === 'published') return { label: 'Published', variant: 'success' };
    if (set.status === 'withdrawn') return { label: 'Withdrawn', variant: 'neutral' };
    if (this.inspecting) return { label: 'Processing', variant: 'info' };
    if (set.inspect?.status === 'failed') return { label: 'Processing failed', variant: 'error' };
    return { label: 'Ready to publish', variant: 'warning' };
  }

  /**
   * api d3e5a155: the inspection's progress by phase. OCR reads title blocks, then a second job finds matchlines with its
   * own page counts. The page in hand is done + 1 (never past the pass's pages); once done reaches pages, or for a phase
   * this build does not know, it is Finishing…, never a frozen "N of N".
   */
  get inspectProgress(): { text: string; percent: number } {
    const inspect = this.set?.inspect;
    const pages = Number(inspect?.pages) || 0;
    const done = Number(inspect?.done) || 0;
    const percent = pages ? Math.min(100, (100 * done) / pages) : 0;
    const label = inspect?.phase === 'ocr' ? 'Reading title blocks' : inspect?.phase === 'matchlines' ? 'Finding matchlines' : null;
    if (!label || (pages && done >= pages)) return { text: 'Finishing…', percent: pages ? 100 : percent };
    return { text: pages ? `${label}: page ${Math.min(done + 1, pages)} of ${pages}…` : `${label}…`, percent };
  }

  get publishBlockedReason(): string {
    if (!this.set) { return 'Loading…'; }
    // Review 2 #2: never ahead of page edits still saving (a Skip would publish), nor over ones that failed
    if (this.pageEditsInFlight || this.bulkBusy) { return 'Saving page changes. Publish once they are saved.'; }
    const failed = this.failedPageEdit;
    if (failed) { return failed; }
    const inspect = this.set.inspect;
    if (this.isInspecting(this.set)) { return this.inspectProgress.text; }
    if (inspect?.status === 'failed') { return this.api.failureMessage(inspect.error ?? undefined); }
    const rows = this.publishRows;
    const amb = rows.find(r => r.match.kind === 'ambiguous');
    if (amb) { return `Pick the sheet for page ${amb.page_number} before publishing.`; }
    const blank = rows.find(r => !r.number?.trim());
    if (blank) { return `Enter a sheet number for page ${blank.page_number} before publishing.`; }
    // The first page whose number another page also uses
    const dup = rows.find(r => this.duplicateNumber(r));
    if (dup) { return `${dup.number.trim()} on page ${dup.page_number} appears twice in this set`; }
    if ((this.set.revisions ?? []).length && !rows.length) { return this.api.failureMessage('DRAWING_SET_ALL_SKIPPED'); }
    return '';
  }

  /** A page change that did not save: retried, discarded or dismissed before the set can publish */
  private get failedPageEdit(): string {
    if (this.bulkFailures.length) {
      const n = this.bulkFailures.length;
      return `${n} ${n === 1 ? "page wasn't" : "pages weren't"} updated. Retry or dismiss the update before publishing.`;
    }
    for (const [uuid, save] of this.saves) {
      if (!save.failedField) { continue; }
      const page = this.set?.revisions?.find(r => r.uuid === uuid)?.page_number;
      return `The ${FIELD_NAMES[save.failedField]} on page ${page ?? '?'} didn't save. Save it again or discard the change before publishing.`;
    }
    return '';
  }

  /** "Skip this page" (W46): left out of the publish and of every check */
  isSkipped(row: DrawingSetRevision): boolean {
    return !!row.skip_publish;
  }

  /** The pages the publish takes: every page not skipped */
  get publishRows(): DrawingSetRevision[] {
    return this.problems.publishRows;
  }

  /** The set's pages, by number key (publishing pages only), and the pages that block the publish, built in one pass */
  private get problems(): ProblemIndex {
    const rows = this.set?.revisions ?? [];
    if (!this.problemIndex || this.problemIndexStale || this.problemIndex.rows !== rows) {
      this.problemIndex = buildProblemIndex(rows);
      this.problemIndexStale = false;
    }
    return this.problemIndex;
  }

  get publishLabel(): string {
    const n = this.publishRows.length;
    return `Publish ${n} ${n === 1 ? 'sheet' : 'sheets'}`;
  }

  /** A page that blocks publishing: no number, an ambiguous match, or a number used twice in the set; never a skipped one */
  needsAttention(row: DrawingSetRevision): boolean {
    return this.problems.problems.has(row);
  }

  get attentionCount(): number {
    return this.problems.problems.size;
  }

  /** The banner's parts (W46): a page can be in two (no number and a sheet to pick), so they may add up to more than the pages */
  get problemCounts(): { pages: number; blank: number; duplicate: number; ambiguous: number } {
    const rows = this.publishRows;
    return {
      pages: this.attentionCount,
      blank: rows.filter(r => !r.number?.trim()).length,
      duplicate: rows.filter(r => this.duplicateNumber(r)).length,
      ambiguous: rows.filter(r => r.match?.kind === 'ambiguous').length,
    };
  }

  /** "8 pages need fixing before you can publish: 6 have no sheet number · 2 share a number · 1 needs a sheet picked" */
  get bannerText(): string {
    const c = this.problemCounts;
    const parts = [
      c.blank ? `${c.blank} ${c.blank === 1 ? 'has' : 'have'} no sheet number` : '',
      c.duplicate ? `${c.duplicate} ${c.duplicate === 1 ? 'shares' : 'share'} a number` : '',
      c.ambiguous ? `${c.ambiguous} ${c.ambiguous === 1 ? 'needs' : 'need'} a sheet picked` : '',
    ].filter(Boolean);
    return `${c.pages} ${c.pages === 1 ? 'page needs' : 'pages need'} fixing before you can publish: ${parts.join(' · ')}`;
  }

  /** The banner and the footer: only once inspection is done, on a draft with something blocking the publish */
  get showProblems(): boolean {
    return !!this.set && this.set.status === 'draft' && !this.inspecting && this.set.inspect?.status !== 'failed' && this.attentionCount > 0;
  }

  /** What the reason badge says (W46): the other page(s) a duplicate number is on; '' for a page that is fine */
  reasonText(row: DrawingSetRevision): string {
    if (!this.needsAttention(row)) {
      return '';
    }
    const number = row.number?.trim();
    if (!number) {
      return 'No sheet number';
    }
    if (this.duplicateNumber(row)) {
      const key = number.toUpperCase();
      const others = (this.problems.byNumber.get(key) ?? []).filter(r => r !== row);
      return others.length === 1 ? `${number} is also on page ${others[0].page_number}` : `${number} is on ${others.length + 1} pages`;
    }
    return 'Pick a sheet';
  }

  /** The grid tile's short form of the reason (the full one is its tooltip): No number, Dup · p.471, Pick sheet */
  tileReasonText(row: DrawingSetRevision): string {
    if (!this.needsAttention(row)) {
      return '';
    }
    const number = row.number?.trim();
    if (!number) {
      return 'No number';
    }
    if (this.duplicateNumber(row)) {
      const key = number.toUpperCase();
      const others = (this.problems.byNumber.get(key) ?? []).filter(r => r !== row);
      return others.length === 1 ? `Dup · p.${others[0].page_number}` : `Dup · ${others.length + 1} pages`;
    }
    return 'Pick sheet';
  }

  /** A page fixed since Needs attention was chosen: it stays in the list, marked Fixed, so rows do not jump */
  isFixed(row: DrawingSetRevision): boolean {
    return this.filter === 'attention' && this.attentionUuids.has(row.uuid) && !this.isSkipped(row) && !this.needsAttention(row);
  }

  /** The pages to fix, in list order: the Needs attention list's still unfixed pages, else every page that blocks the publish */
  problemRows(): DrawingSetRevision[] {
    return this.visibleRows.filter(r => this.needsAttention(r));
  }

  /** The panel's "Issue n of N" for a page to fix; null for any other page */
  issuePosition(row: DrawingSetRevision): { index: number; total: number } | null {
    const problems = this.problemRows();
    const index = problems.indexOf(row);
    return index < 0 ? null : { index, total: problems.length };
  }

  /** Filtered to Needs attention: Previous / Next walk only the pages still to fix (W46) */
  get filteredToProblems(): boolean {
    return this.filter === 'attention';
  }

  /** A page the panel's Previous / Next stop on: every page, or only those still to fix while filtered */
  walkable(row: DrawingSetRevision): boolean {
    return this.filter !== 'attention' || this.needsAttention(row);
  }

  /** The panel's line once Next has nowhere to go while filtered; '' otherwise */
  get problemsLeftText(): string {
    if (this.filter !== 'attention' || this.problemRows().length) {
      return '';
    }
    const left = this.attentionCount;
    return left ? `${left} left` : 'All issues fixed — ready to publish';
  }

  /**
   * Enter in the panel's Number field (W46): once the save has landed (and the set reloaded with the new match), a page
   * that no longer blocks the publish hands over to the next page to fix. A page that was fine to begin with stays.
   */
  advanceWhenFixed(row: DrawingSetRevision): void {
    this.advanceUuid = this.needsAttention(row) ? row.uuid : null;
  }

  /** The panel's save line for the page: Saving…, Saved (naming the one field changed) or the error */
  saveStatus(row: DrawingSetRevision): PageSaveState | null {
    return this.saves.get(row.uuid)?.state ?? null;
  }

  /** The field whose save failed keeps its typed value and a red underline */
  saveFailed(row: DrawingSetRevision, field: string): boolean {
    const failed = this.saves.get(row.uuid)?.failedField;
    return !!failed && (failed === field || FIELD_NAMES[failed] === FIELD_NAMES[field]);
  }

  /** Emits once the page has no save in flight: true when its last save landed, false when one failed */
  whenSaved(row: DrawingSetRevision): Observable<boolean> {
    const settled = () => this.saves.get(row.uuid)?.state.status !== 'error';
    if (!this.saves.get(row.uuid)?.inFlight) {
      return of(settled());
    }
    return this.saveSettled.pipe(where(uuid => uuid === row.uuid), first(), map(settled), takeUntil(this.onDestroy));
  }

  /** "Review 8 pages": Needs attention, from its first list page, with the first page to fix in the panel */
  reviewProblems(source: 'banner' | 'footer' = 'banner'): void {
    this.track.track('review_banner_clicked', { set_uuid: this.setUuid, needs_attention: this.attentionCount, source });
    this.setFilter('attention');
    const first = this.visibleRows.find(r => this.needsAttention(r));
    if (first) {
      this.openPage(first, 'problem_link');
    }
  }

  /** The pages the filter shows, in set order */
  get visibleRows(): DrawingSetRevision[] {
    const rows = this.set?.revisions ?? [];
    return this.filter === 'attention' ? rows.filter(r => this.attentionUuids.has(r.uuid)) : rows;
  }

  /** The current list or grid page of `visibleRows` */
  get pageRows(): DrawingSetRevision[] {
    const rows = this.visibleRows;
    const last = Math.max(0, Math.ceil(rows.length / this.pageSize) - 1);
    const index = Math.min(this.pageIndex, last);
    return rows.slice(index * this.pageSize, (index + 1) * this.pageSize);
  }

  setFilter(filter: 'all' | 'attention'): void {
    this.filter = filter;
    this.attentionUuids = new Set((this.set?.revisions ?? []).filter(r => this.needsAttention(r)).map(r => r.uuid));
    this.pageIndex = 0;
  }

  /** The filter pill */
  chooseFilter(filter: 'all' | 'attention'): void {
    this.setFilter(filter);
    this.track.track('review_filter_changed', { set_uuid: this.setUuid, filter });
  }

  /** The List / Grid toggle */
  setView(view: 'list' | 'grid'): void {
    if (view !== this.viewMode) {
      this.viewMode = view;
      this.track.track('review_view_changed', { set_uuid: this.setUuid, view });
    }
  }

  onPage(event: { pageIndex: number }): void {
    this.pageIndex = event.pageIndex;
  }

  /** Sri: the whole row (or tile) opens the page's review; its checkbox and "…" menu do their own thing */
  onRowClick(event: Event, row: DrawingSetRevision, source: 'row' | 'tile' = 'row'): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, select, textarea, button, a, mat-checkbox, .tile-check, .mat-mdc-menu-trigger')) {
      return;
    }
    void source;
    this.openReview(row);
  }

  /** Enter or Space on a focused row opens its review */
  onRowKey(event: KeyboardEvent, row: DrawingSetRevision): void {
    if ((event.key === 'Enter' || event.key === ' ') && event.target === event.currentTarget) {
      event.preventDefault();
      this.openReview(row);
    }
  }

  /** The page under review (Sri), while it is in the set */
  get reviewRow(): DrawingSetRevision | null {
    return (this.reviewUuid && this.set?.revisions?.find(r => r.uuid === this.reviewUuid)) || null;
  }

  /** "Page 2 of 14", through the pages the Show filter shows */
  get reviewPosition(): string {
    const row = this.reviewRow;
    const rows = this.visibleRows;
    const i = row ? rows.indexOf(row) : -1;
    return i < 0 ? `Page ${row?.page_number ?? ''}`.trim() : `Page ${i + 1} of ${rows.length}`;
  }

  /** The page before (-1) or after (1) the reviewed one among those shown; null at either end or off the filter */
  reviewNeighbour(step: 1 | -1): DrawingSetRevision | null {
    const rows = this.visibleRows;
    const i = this.reviewRow ? rows.indexOf(this.reviewRow) : -1;
    return i < 0 ? null : rows[i + step] ?? null;
  }

  reviewStep(step: 1 | -1): void {
    const next = this.reviewNeighbour(step);
    if (next) {
      this.openReview(next);
      // The side panel follows the page under review
      this.panel?.show(next.uuid);
    }
  }

  /**
   * The page's review: a page replacing a sheet opens on the Overlay compare against that sheet's current revision; a
   * new sheet is the page itself. ‹ Prev / Next ›, Include / Skip and Edit details sit above it; the set's footer stays.
   */
  openReview(row: DrawingSetRevision): void {
    this.reviewUuid = row.uuid;
    this.selectedUuid = row.uuid;
    this.showRow(row);
    this.reviewAgainst = null;
    const sheetUuid = row.match?.kind === 'replaces' ? row.match.sheetUuid : null;
    if (sheetUuid) {
      const known = this.againstCache.get(sheetUuid);
      if (known !== undefined) {
        this.reviewAgainst = known;
      } else {
        this.api.getSheet(this.projectId, sheetUuid).pipe(takeUntil(this.onDestroy)).subscribe({
          next: sheet => {
            const against = sheet?.current?.uuid ?? null;
            this.againstCache.set(sheetUuid, against);
            if (this.reviewUuid === row.uuid) {
              this.reviewAgainst = against;
              this.cdr.markForCheck();
            }
          },
          error: e => this.notify.notification(this.api.requestFailure(e), 'error'),
        });
      }
    }
    this.cdr.markForCheck();
  }

  /** Back to the list (a one-page revision does not open its review again) */
  backToPages(): void {
    this.reviewUuid = null;
    this.reviewAgainst = null;
    this.autoReviewed = true;
    this.cdr.markForCheck();
  }

  /** A one-page draft replacing a sheet (Upload new revision) opens straight into its Preview changes, once */
  private maybeAutoReview(): void {
    const s = this.set;
    if (this.autoReviewed || !s || s.status !== 'draft' || this.isInspecting(s) || s.revisions?.length !== 1) {
      return;
    }
    const [only] = s.revisions;
    this.autoReviewed = true;
    if (only.match?.kind === 'replaces' && only.match.sheetUuid && !this.isSkipped(only)) {
      this.openReview(only);
    }
  }

  /**
   * The page side panel (W42), in the app's record side panel as RFIs and issues use it. With the panel already open
   * the page just changes there, keeping its place.
   */
  openPage(row: DrawingSetRevision, source: 'row' | 'tile' | 'problem_link' = 'row'): void {
    this.selectedUuid = row.uuid;
    if (!this.panel && !this.panelRequested) {
      this.track.track('page_panel_opened', { set_uuid: this.setUuid, source });
    }
    if (this.panel) {
      this.panel.show(row.uuid);
      return;
    }
    // Another row clicked before the panel is up: it opens on that one
    if (this.panelRequested && this.pendingPanel) {
      this.pendingPanel.uuid = row.uuid;
      return;
    }
    this.panelRequested = true;
    this.pendingPanel = { host: this, rows: () => this.visibleRows, uuid: row.uuid };
    this.projects.openRightPanel.next({ component: DrawingSetPagePanelComponent, data: this.pendingPanel });
  }

  attachPanel(panel: DrawingSetPagePanelComponent | null): void {
    if (panel) {
      this.panelRequested = false;
      this.pendingPanel = null;
      if (this.destroyed) {
        // The review went while the panel was on its way (I1): it must not outlive the review
        this.projects.closeRightPanel.next();
        return;
      }
    }
    const closing = !!this.panel && !panel;
    this.panel = panel;
    if (closing && !this.destroyed) {
      this.returnFocus();
    }
  }

  /** PageUp / PageDown (I3): from the panel or this review only, and not while the full-page viewer is open */
  pageKeysAllowed(target: EventTarget | null, inPanel: boolean): boolean {
    if (this.dialog.openDialogs.length) {
      return false;
    }
    return inPanel || (target instanceof Node && this.elementRef.nativeElement.contains(target));
  }

  /** The panel moved to `row` (Prev / Next, a drop): highlight it and keep it on the visible list page */
  pageShown(row: DrawingSetRevision | null): void {
    // A page change clears the Saved lines; an error stays with its page
    for (const [uuid, save] of this.saves) {
      if (uuid !== row?.uuid && !save.inFlight && save.state.status === 'saved') {
        this.saves.delete(uuid);
      }
    }
    if (row) {
      this.focusReturnUuid = row.uuid;
    }
    this.selectedUuid = row?.uuid ?? null;
    if (row) {
      this.showRow(row);
    }
    // The page under review follows the panel
    if (row && this.reviewUuid && this.reviewUuid !== row.uuid) {
      this.openReview(row);
    }
    this.cdr.markForCheck();
  }

  /** Focus goes back to the row (or tile) of the page the panel showed last */
  private returnFocus(): void {
    const uuid = this.focusReturnUuid;
    this.focusReturnUuid = null;
    if (!uuid) {
      return;
    }
    this.cdr.detectChanges();
    const target = this.elementRef.nativeElement.querySelector(`[data-uuid="${CSS.escape(uuid)}"]`) as HTMLElement | null;
    target?.focus({ preventScroll: false });
  }

  /** The full-page viewer: zoom and pan */
  openViewer(row: DrawingSetRevision): void {
    this.track.track('page_viewer_opened', { set_uuid: this.setUuid, mode: 'whole' });
    this.dialog.open(DrawingSetPageViewerComponent, {
      data: { row, fileUrl: this.set?.file_url ?? null }, maxWidth: '90vw', maxHeight: '90vh', panelClass: 'drawing-set-page-panel', autoFocus: false,
    });
  }

  /** The row's title-block crop (W43): the page image zoomed on its lower-right corner, as the W28 card preview */
  titleBlockCrop(row: DrawingSetRevision): SheetPreviewCrop {
    const aspect = this.pageAspect.get(row.uuid) ?? DEFAULT_PAGE_ASPECT;
    const cached = this.crops.get(row.uuid);
    if (cached?.aspect === aspect) {
      return cached.crop;
    }
    const crop = sheetPreviewCrop(TITLE_BLOCK_CROP_POINT, aspect, TITLE_BLOCK_BOX_ASPECT, TITLE_BLOCK_CROP_ZOOM);
    this.crops.set(row.uuid, { aspect, crop });
    return crop;
  }

  onCropLoad(row: DrawingSetRevision, image: HTMLImageElement): void {
    if (image.naturalWidth && image.naturalHeight) {
      this.pageAspect.set(row.uuid, image.naturalWidth / image.naturalHeight);
      this.cdr.markForCheck();
    }
  }

  /** "begin – 1006+00", "1006+00 – 1018+00", "1066+00 – end"; '' with neither end */
  stationRangeText(row: DrawingSetRevision): string {
    const has = (v: number | null | undefined) => v !== null && v !== undefined;
    if (!has(row.station_start) && !has(row.station_end)) {
      return '';
    }
    return `${has(row.station_start) ? stationInputText(row.station_start) : 'begin'} – ${has(row.station_end) ? stationInputText(row.station_end) : 'end'}`;
  }

  // Bulk edit (W43) -----------------------------------------------------------------------------------------------

  get canBulkEdit(): boolean {
    return this.editable;
  }

  isChecked(row: DrawingSetRevision): boolean {
    return this.checked.has(row.uuid);
  }

  toggleChecked(row: DrawingSetRevision, on: boolean): void {
    if (on) {
      this.checked.add(row.uuid);
    } else {
      this.checked.delete(row.uuid);
    }
  }

  /** A press on a row or tile checkbox: remembers Shift for the change that follows */
  checkPress(event: MouseEvent | KeyboardEvent): void {
    this.shiftHeld = event.shiftKey;
  }

  /**
   * A row or tile checkbox changed (item 8): with Shift held, every page from the last one checked to this one takes
   * the same state, in list order, in either view.
   */
  checkFromBox(row: DrawingSetRevision, on: boolean): void {
    const rows = this.visibleRows;
    const from = this.shiftHeld && this.lastChecked ? rows.findIndex(r => r.uuid === this.lastChecked) : -1;
    const to = rows.indexOf(row);
    this.shiftHeld = false;
    if (from >= 0 && to >= 0) {
      for (const r of rows.slice(Math.min(from, to), Math.max(from, to) + 1)) {
        this.toggleChecked(r, on);
      }
      this.track.track('bulk_selection_changed', { set_uuid: this.setUuid, method: 'range', selected: this.checked.size, view: this.viewMode });
    } else {
      this.toggleChecked(row, on);
    }
    this.lastChecked = row.uuid;
  }

  get pageAllChecked(): boolean {
    const rows = this.pageRows;
    return !!rows.length && rows.every(r => this.checked.has(r.uuid));
  }

  get pageSomeChecked(): boolean {
    return this.pageRows.some(r => this.checked.has(r.uuid)) && !this.pageAllChecked;
  }

  /** The header checkbox: every page on this list page */
  togglePage(on: boolean): void {
    for (const r of this.pageRows) {
      this.toggleChecked(r, on);
    }
  }

  /** The list runs past this screen (more pages than one list page): the select-all offer has something to add */
  get moreThanOnScreen(): boolean {
    return this.visibleRows.length > this.pageRows.length;
  }

  get setAllChecked(): boolean {
    const rows = this.visibleRows;
    return !!rows.length && rows.every(r => this.checked.has(r.uuid));
  }

  /** "Select all N": every page the list shows (the filter applied), across its list pages */
  checkAllInSet(): void {
    for (const r of this.visibleRows) {
      this.checked.add(r.uuid);
    }
    this.track.track('bulk_selection_changed', { set_uuid: this.setUuid, method: 'all', selected: this.checked.size, view: this.viewMode });
  }

  clearChecked(): void {
    this.checked.clear();
    this.bulkFailures = [];
    this.bulkDone = 0;
  }

  /** The bar's patch: only the fields given a value; a type of "other" is sent as null */
  get bulkPatch(): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    if (this.bulkDiscipline.trim()) {
      patch.discipline = this.bulkDiscipline.trim();
    }
    if (this.bulkType) {
      patch.sheet_type = this.bulkType === 'other' ? null : this.bulkType;
    }
    if (this.bulkLabel.trim()) {
      patch.label = this.bulkLabel.trim();
    }
    if (this.bulkPublish) {
      patch.skip_publish = this.bulkPublish === 'skip';
    }
    return patch;
  }

  get hasBulkChange(): boolean {
    return Object.keys(this.bulkPatch).length > 0;
  }

  /**
   * Applies the bar to every checked page with the per-revision PUT, BULK_CONCURRENCY at a time. Each saved row is
   * updated in place; pages that failed stay checked and are listed with the reason, for Retry.
   */
  applyBulk(rows: DrawingSetRevision[] = (this.set?.revisions ?? []).filter(r => this.checked.has(r.uuid))): void {
    const patch = this.bulkPatch;
    if (!this.canBulkEdit || this.bulkBusy || !rows.length || !Object.keys(patch).length) {
      return;
    }
    this.bulkBusy = true;
    this.bulkDone = 0;
    this.bulkFailures = [];
    from(rows)
      .pipe(
        mergeMap(row => this.api.updateSetRevision(this.projectId, this.setUuid, row.uuid, patch).pipe(
          map(updated => ({ row, updated, message: null as string | null })),
          catchError((e: HttpErrorResponse) => of({ row, updated: null, message: this.api.requestFailure(e, { save: true }) })),
        ), BULK_CONCURRENCY),
        toArray(),
        takeUntil(this.onDestroy),
      )
      .subscribe(results => {
        const failed = results.filter(r => r.message).length;
        this.track.track('bulk_applied', {
          set_uuid: this.setUuid, selected: rows.length, fields: Object.keys(patch), succeeded: results.length - failed, failed,
        });
        if (patch.skip_publish !== undefined && results.length > failed) {
          this.track.track('page_skip_toggled', { set_uuid: this.setUuid, skip: patch.skip_publish, source: 'bulk' });
        }
        for (const r of results) {
          if (r.message) {
            this.bulkFailures.push({ row: r.row, message: r.message });
          } else {
            if (r.updated) {
              Object.assign(r.row, r.updated, { match: r.row.match });
              this.problemIndexStale = true;
            }
            this.checked.delete(r.row.uuid);
            this.bulkDone++;
          }
        }
        this.bulkBusy = false;
        if (!this.bulkFailures.length) {
          this.bulkDiscipline = '';
          this.bulkType = '';
          this.bulkLabel = '';
          this.bulkPublish = '';
          this.notify.notification(`${this.bulkDone} ${this.bulkDone === 1 ? 'page' : 'pages'} updated`, 'success');
        }
        this.cdr.markForCheck();
      });
  }

  /** "5 updated · 2 couldn't be updated." or "7 pages couldn't be updated." */
  get bulkFailureSummary(): string {
    const n = this.bulkFailures.length;
    return this.bulkDone ? `${this.bulkDone} updated · ${n} couldn't be updated.` : `${n} ${n === 1 ? "page couldn't" : "pages couldn't"} be updated.`;
  }

  /** The failures grouped by reason, every page listed: one line when they all failed the same way */
  get bulkFailureGroups(): { pages: number[]; message: string }[] {
    const groups = new Map<string, number[]>();
    for (const f of this.bulkFailures) {
      groups.set(f.message, [...(groups.get(f.message) ?? []), f.row.page_number]);
    }
    return [...groups].map(([message, pages]) => ({ message, pages }));
  }

  /** The ✕ on the failure box; the failed pages stay checked */
  dismissBulkFailures(): void {
    this.bulkFailures = [];
    this.bulkDone = 0;
  }

  retryBulkFailures(): void {
    this.track.track('bulk_retried', { set_uuid: this.setUuid, count: this.bulkFailures.length });
    this.applyBulk(this.bulkFailures.map(f => f.row));
  }

  /** Moves the list to the page holding `row` */
  private showRow(row: DrawingSetRevision): void {
    const index = this.visibleRows.indexOf(row);
    if (index >= 0) {
      this.pageIndex = Math.floor(index / this.pageSize);
    }
  }

  /** The tile image: the thumbnail, else the page preview */
  tileImage(row: DrawingSetRevision): string | null {
    return row.thumb_url || row.preview_url || null;
  }

  /** Rows keep their DOM (inputs, focus, an open select) across polls. */
  trackRow(_index: number, row: DrawingSetRevision): string {
    return row.uuid;
  }

  editingStarted(row: DrawingSetRevision): void {
    this.editingUuid = row.uuid;
  }

  editingEnded(row: DrawingSetRevision): void {
    if (this.editingUuid === row.uuid) {
      this.editingUuid = null;
    }
  }

  /** After the poll gave up: ask again. */
  retryPoll(): void {
    this.startPoll();
  }

  /** Another page that publishes has the same number; a skipped page neither has nor makes a duplicate */
  duplicateNumber(row: DrawingSetRevision): boolean {
    const key = numberKey(row);
    return !!key && !this.isSkipped(row) && (this.problems.byNumber.get(key)?.length ?? 0) > 1;
  }

  /** Text field commit: skips unchanged values; number and label cannot be cleared (the API rejects ''). */
  commit(row: DrawingSetRevision, field: 'number' | 'title' | 'label' | 'discipline', input: HTMLInputElement): void {
    const value = input.value.trim();
    if (value === (row[field] ?? '')) {
      this.clearFailure(row, field);
      this.settleAdvance(row);
      return;
    }
    if (!value && (field === 'number' || field === 'label')) {
      input.value = row[field] ?? '';
      this.clearFailure(row, field);
      this.settleAdvance(row);
      return;
    }
    this.edit(row, { [field]: value });
  }

  /** Station cell commit: typed as 120+00 (or feet), sent in feet; blank clears it. */
  commitStation(row: DrawingSetRevision, field: 'station_start' | 'station_end', input: HTMLInputElement): void {
    const value = parseStationInput(input.value);
    if (value === undefined) {
      input.value = stationInputText(row[field]);
      this.notify.notification('Enter a station like 120+00.', 'error');
      return;
    }
    input.value = stationInputText(value);
    if (value === (row[field] ?? null)) {
      // Back to the saved station: nothing is left unsaved (review I4)
      this.clearFailure(row, field);
      return;
    }
    this.edit(row, { [field]: value });
  }

  /** Type select: null stays null while "Other" is shown, so only a real change is sent */
  commitType(row: DrawingSetRevision, value: string): void {
    const type = SHEET_TYPES.some(t => t.value === value) ? (value as SheetType) : null;
    if (type === (row.sheet_type ?? 'other')) {
      this.clearFailure(row, 'sheet_type');
      return;
    }
    this.edit(row, { sheet_type: type });
  }

  /**
   * An empty station cell's hint: the title block's suggestion when there is one; "begin" or "end" when only the other
   * end is set (an open-ended range, R39); otherwise a neutral "STA", never an example that reads as data.
   */
  stationPlaceholder(row: DrawingSetRevision, field: 'station_start' | 'station_end'): string {
    const suggested = field === 'station_start' ? row.suggested_station_start : row.suggested_station_end;
    if (suggested !== null && suggested !== undefined && row[field] === null) {
      return stationInputText(suggested);
    }
    const other = field === 'station_start' ? row.station_end : row.station_start;
    if (other !== null && other !== undefined) {
      return field === 'station_start' ? 'begin' : 'end';
    }
    return 'STA';
  }

  /** Only a real title-block suggestion is shown as a hint (italic): not an open end, not the empty "STA" */
  stationIsSuggestion(row: DrawingSetRevision, field: 'station_start' | 'station_end'): boolean {
    const suggested = field === 'station_start' ? row.suggested_station_start : row.suggested_station_end;
    return row[field] === null && suggested !== null && suggested !== undefined;
  }

  /** What an empty station cell's hint means, for screen readers; null when the cell has a value or shows an example */
  stationHintNote(row: DrawingSetRevision, field: 'station_start' | 'station_end'): string | null {
    if (row[field] !== null && row[field] !== undefined) {
      return null;
    }
    const hint = this.stationPlaceholder(row, field);
    if (hint === 'begin' || hint === 'end') {
      return `Open range: runs to the ${hint === 'begin' ? 'beginning' : 'end'} of the project. Not a saved value.`;
    }
    const suggested = field === 'station_start' ? row.suggested_station_start : row.suggested_station_end;
    return suggested !== null && suggested !== undefined ? `Suggested from the title block (${hint}). Not saved; type it to keep it.` : null;
  }

  edit(row: DrawingSetRevision, patch: Record<string, unknown>): void {
    const field = Object.keys(patch).find(k => FIELD_NAMES[k]) ?? null;
    if (field) {
      this.saveStarted(row, field);
    }
    const wasProblem = this.needsAttention(row);
    this.pageEditsInFlight++;
    this.api.updateSetRevision(this.projectId, this.setUuid, row.uuid, patch).pipe(finalize(() => {
      this.pageEditsInFlight = Math.max(0, this.pageEditsInFlight - 1);
      this.cdr.markForCheck();
    })).subscribe({
      next: updated => {
        if (!updated) {
          if (patch.drop) {
            this.track.track('page_dropped', { set_uuid: this.setUuid });
          }
          this.saves.delete(row.uuid);
          this.set.revisions = this.set.revisions.filter(r => r !== row);
          this.problemIndexStale = true;
          return;
        }
        Object.assign(row, updated, { match: row.match });
        this.problemIndexStale = true;
        if (field) {
          this.saveEnded(row, field, null);
        }
        this.trackSaved(row, patch, field, wasProblem);
        if (patch.number !== undefined || patch.sheetUuid !== undefined) {
          // The new match (an ambiguous one blocks too) comes with the reload
          this.reload(() => this.settleAdvance(row));
        } else {
          this.settleAdvance(row);
        }
      },
      error: (e: HttpErrorResponse) => {
        this.settleAdvance(null);
        if (field && TRACKED_FIELDS[field]) {
          this.track.track('page_field_save_failed', { set_uuid: this.setUuid, field: TRACKED_FIELDS[field], source: 'panel', ...errorProps(e) });
        } else if (field || patch.drop) {
          this.track.error('set_review', e);
        }
        if (field) {
          // Said under the field in the panel; the page stays open until it saves
          const name = FIELD_NAMES[field];
          if (field === 'skip_publish') {
            // The checkbox keeps the user's tick; the row (unchanged) is the truth (review I4)
            this.panel?.resyncSkip(row);
          }
          this.saveEnded(row, field, this.api.isOffline(e)
            ? `Couldn't save the ${name}. Check your connection and try again.`
            : `Couldn't save the ${name}. ${this.api.requestFailure(e, { save: true })}`);
        } else {
          this.fail(e);
        }
      },
    });
  }

  /** W48: a page save that landed: the field, or the skip, or the drop */
  private trackSaved(row: DrawingSetRevision, patch: Record<string, unknown>, field: string | null, wasProblem: boolean): void {
    if (patch.skip_publish !== undefined) {
      this.track.track('page_skip_toggled', { set_uuid: this.setUuid, skip: patch.skip_publish, source: 'panel' });
    } else if (field && TRACKED_FIELDS[field]) {
      this.track.track('page_field_saved', {
        set_uuid: this.setUuid, field: TRACKED_FIELDS[field], source: 'panel', fixed_problem: wasProblem && !this.needsAttention(row),
      });
    }
  }

  /** The panel stepped with Previous / Next (PageUp / PageDown) */
  pageStepped(direction: 'next' | 'prev'): void {
    this.track.track('issue_navigated', {
      set_uuid: this.setUuid, direction, filtered: this.filter === 'attention', remaining: this.problemRows().length,
    });
  }

  /** "Skip this page", saved straight away with the page's other fields */
  setSkip(row: DrawingSetRevision, skip: boolean): void {
    if (skip !== this.isSkipped(row)) {
      this.edit(row, { skip_publish: skip });
    } else {
      // Ticked back to what is saved: nothing is left unsaved (review I4)
      this.clearFailure(row, 'skip_publish');
    }
  }

  /**
   * "Discard change" on a failed save (review I4): the page goes back to its saved values, so Previous / Next are free
   * again. The panel re-draws the fields from the row.
   */
  discardFailedSave(row: DrawingSetRevision): void {
    const save = this.saves.get(row.uuid);
    if (save?.inFlight) {
      // Review N1: a save still on its way keeps its entry, so Next goes on waiting and a later failure still shows
      save.failedField = null;
      save.errorText = '';
      save.state = { status: 'saving', text: 'Saving…' };
    } else {
      this.saves.delete(row.uuid);
      this.saveSettled.next(row.uuid);
    }
    this.cdr.markForCheck();
  }

  pickCandidate(row: DrawingSetRevision, sheetUuid: string | null): void {
    this.edit(row, { sheetUuid });
  }

  drop(row: DrawingSetRevision): void {
    this.edit(row, { drop: true });
  }

  /** Publish asks first (item 6): the summary and the change notes, which are saved with the publish */
  /** Discard set (⋯): a set not yet published can go, while nothing is publishing */
  get canDiscard(): boolean {
    return this.editable && !this.publishing && !this.discarding;
  }

  discarding = false;

  /** Confirms, then deletes the draft set and its pages and goes back to Drawings */
  discard(): void {
    if (!this.canDiscard || !this.set) { return; }
    const pages = this.set.revisions?.length ?? 0;
    confirmAndDiscardSet(this.dialog, {
      discardSet: (projectId, setUuid) => {
        this.discarding = true;
        this.cdr.markForCheck();
        return this.api.discardSet(projectId, setUuid).pipe(finalize(() => { this.discarding = false; this.cdr.markForCheck(); }));
      },
    }, this.projectId, { setUuid: this.setUuid, name: this.reviewTitle, pages }).pipe(takeUntil(this.onDestroy)).subscribe({
      next: () => {
        this.track.track('set_discarded', { set_uuid: this.setUuid, pages, source: 'set' });
        this.notify.notification('Set discarded', 'success');
        this.back.emit();
      },
      error: e => this.notify.notification(this.api.requestFailure(e), 'error'),
    });
  }

  publish(): void {
    if (!this.canPublish || this.publishBlockedReason || this.publishing || !this.set) { return; }
    const rows = this.publishRows;
    const data: DrawingPublishDialogData = {
      projectId: this.projectId,
      setUuid: this.setUuid,
      notes: this.set.notes,
      canEditNotes: this.canConfigure,
      publishCount: rows.length,
      skippedCount: (this.set.revisions ?? []).length - rows.length,
      replaceCount: rows.filter(r => r.match?.kind === 'replaces').length,
      warnings: {},
      blockedReason: () => this.publishBlockedReason,
    };
    // What the confirmation lists if the API asks for the warnings to be acknowledged (409 with the pages)
    for (const r of rows) {
      const warnings = this.warningTexts(r);
      if (warnings) {
        data.warnings[r.uuid] = { page: `Page ${r.page_number}${r.number ? ' · ' + r.number : ''}`, warnings };
      }
    }
    const counts = { set_uuid: this.setUuid, sheets: data.publishCount, skipped: data.skippedCount, replaces: data.replaceCount };
    this.track.track('publish_dialog_opened', counts);
    const ref = this.dialog
      .open<DrawingPublishDialogComponent, DrawingPublishDialogData, DrawingPublishDialogResult | null>(DrawingPublishDialogComponent, {
        data, panelClass: ['floating-dialog-box', 'drawing-publish-dialog'], autoFocus: false,
      });
    ref.afterClosed()
      .pipe(takeUntil(this.onDestroy))
      .subscribe(result => {
        if (!result?.set) {
          // Dismissed (review N3): keep notes the dialog saved, and read the set again only when a publish got no
          // answer and may have gone through; a plain open-and-close fetches nothing
          const dialog = ref.componentInstance;
          if (dialog?.savedNotes !== null && dialog?.savedNotes !== undefined && this.set) {
            this.set.notes = dialog.savedNotes;
          }
          if (dialog?.publishUncertain) {
            this.reload();
          }
          return;
        }
        this.set.notes = result.notes;
        this.publishing = true;
        this.track.track('set_published', {
          ...counts, has_notes: !!result.notes?.trim(),
          review_duration_s: this.openedAt === null ? null : Math.round((Date.now() - this.openedAt) / 1000),
        });
        const count = result.set?.published?.length ?? 0;
        this.notify.notification(`Published ${count} ${count === 1 ? 'sheet' : 'sheets'}`, 'success');
        this.afterPublish(result.set);
      });
  }

  labelPlaceholder(row: DrawingSetRevision): string {
    return (row.match.kind === 'replaces' && row.match.suggestedLabel) || 'A';
  }

  /** W65: "New revision of 4 · PROJECT LAYOUT (replaces Rev A)" while the set is a draft; the label is read live */
  get targetSheetText(): string {
    const t = this.set?.target_sheet;
    if (!t || this.set.status !== 'draft') {
      return '';
    }
    const name = [t.number, t.title].filter(Boolean).join(' · ');
    return `New revision of ${name}${t.current_label ? ` (replaces Rev ${t.current_label})` : ''}`;
  }

  matchLabel(row: DrawingSetRevision): string {
    switch (row.match.kind) {
      case 'replaces': return `Replaces ${row.match.sheetNumber}${row.match.currentLabel ? ' Rev ' + row.match.currentLabel : ''}`;
      case 'ambiguous': return 'Ambiguous';
      default: return 'New sheet';
    }
  }

  /**
   * Counts the shared redlines left on the revisions this publish superseded. With some, the review stays open on the
   * published set with a banner to carry-forward; with none (or if counting fails) it goes back to the register.
   */
  private afterPublish(s: DrawingIssueSet): void {
    const superseded = (s?.published ?? []).filter(p => p.supersededRevisionUuid);
    const done = (count: number) => {
      this.publishing = false;
      this.carryCount = count;
      if (count > 0) {
        this.applySet(s);
      } else {
        this.published.emit(s);
      }
    };
    if (!superseded.length) {
      done(0);
      return;
    }
    forkJoin(superseded.map(p => this.api.carryForward(this.projectId, p.sheetUuid))).pipe(takeUntil(this.onDestroy)).subscribe({
      next: offers => done(offers.reduce((n, o) => n + (o?.items?.length ?? 0), 0)),
      error: () => done(0),
    });
  }

  /**
   * Fetches the set now and every POLL_MS while it inspects. A failed fetch is retried with backoff; only
   * MAX_POLL_FAILURES in a row stop the poll, with a message and a Retry.
   */
  private startPoll(): void {
    this.poll?.unsubscribe();
    this.pollError = '';
    this.poll = defer(() => this.api.getSet(this.projectId, this.setUuid))
      .pipe(
        retry({ count: MAX_POLL_FAILURES - 1, delay: (_e, attempt) => timer(POLL_MS * 2 ** (attempt - 1)) }),
        repeat({ delay: POLL_MS }),
        takeWhile(s => this.isInspecting(s), true),
        takeUntil(this.onDestroy),
      )
      .subscribe({
        next: s => this.applySet(s),
        error: () => {
          this.pollError = 'Inspection progress stopped updating. Check your connection and try again.';
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Keeps the existing row objects (merged in place, the typed fields of the
   * row being edited left alone), so the grid keeps its inputs and focus.
   */
  private applySet(s: DrawingIssueSet): void {
    const before = this.set;
    if (s?.revisions && this.set?.revisions) {
      const existing = new Map(this.set.revisions.map(r => [r.uuid, r]));
      s.revisions = s.revisions.map(incoming => {
        const row = existing.get(incoming.uuid);
        if (!row) {
          return incoming;
        }
        if (row.uuid === this.editingUuid) {
          // Only the fields typed into stay as they are; the match, thumbnail and warnings still land.
          const typed = Object.fromEntries(TYPED_FIELDS.map(field => [field, row[field]]));
          Object.assign(row, incoming, typed);
        } else {
          Object.assign(row, incoming);
        }
        return row;
      });
    }
    this.set = s;
    this.problemIndexStale = true;
    if (this.reviewUuid && !this.reviewRow) {
      // The reviewed page was dropped
      this.reviewUuid = null;
    }
    this.maybeAutoReview();
    this.trackSetLoaded(before);
    this.cdr.markForCheck();
  }

  private isInspecting(s: DrawingIssueSet | null): boolean {
    return s?.inspect?.status === 'queued' || s?.inspect?.status === 'running';
  }

  private reload(done?: () => void): void {
    this.api.getSet(this.projectId, this.setUuid).pipe(takeUntil(this.onDestroy)).subscribe({
      next: s => {
        this.applySet(s);
        done?.();
      },
      error: e => {
        // Review M3: an Enter that was waiting on this reload must not advance on a later, unrelated save
        this.advanceUuid = null;
        this.fail(e);
      },
    });
  }

  /** The save after Enter has landed (or there was nothing to save): a page fixed now hands over to the next to fix */
  private settleAdvance(row: DrawingSetRevision | null): void {
    if (!this.advanceUuid || (row && row.uuid !== this.advanceUuid)) {
      return;
    }
    this.advanceUuid = null;
    // Only while the panel still shows that page: a click elsewhere meanwhile wins
    if (!row || !this.panel || this.selectedUuid !== row.uuid || this.needsAttention(row)) {
      return;
    }
    // Filtered: the next page still to fix; all pages: the next page
    this.panel.next();
    this.cdr.markForCheck();
  }

  private saveStarted(row: DrawingSetRevision, field: string): void {
    const save = this.saves.get(row.uuid) ?? { inFlight: 0, fields: new Set<string>(), failedField: null, errorText: '', state: null };
    if (!save.inFlight) {
      // A new edit: the line names what this round saves
      save.fields.clear();
    }
    save.inFlight++;
    save.fields.add(FIELD_NAMES[field]);
    save.state = { status: 'saving', text: 'Saving…' };
    this.saves.set(row.uuid, save);
  }

  private saveEnded(row: DrawingSetRevision, field: string, error: string | null): void {
    const save = this.saves.get(row.uuid);
    if (!save) {
      return;
    }
    save.inFlight = Math.max(0, save.inFlight - 1);
    if (error) {
      save.failedField = field;
      save.errorText = error;
    } else if (save.failedField && FIELD_NAMES[save.failedField] === FIELD_NAMES[field]) {
      save.failedField = null;
    }
    if (save.inFlight) {
      return;
    }
    if (save.failedField) {
      // A failed field stays the line until it saves, whatever else saved meanwhile
      save.state = { status: 'error', text: save.errorText };
    } else {
      const names = [...save.fields];
      const one = names.length === 1 ? names[0] : '';
      save.state = { status: 'saved', text: one ? `${one[0].toUpperCase()}${one.slice(1)} saved` : 'Saved' };
    }
    this.saveSettled.next(row.uuid);
    this.cdr.markForCheck();
  }

  /** The field is back to its saved value: nothing is left unsaved there */
  private clearFailure(row: DrawingSetRevision, field: string): void {
    const save = this.saves.get(row.uuid);
    if (save?.failedField && FIELD_NAMES[save.failedField] === FIELD_NAMES[field]) {
      this.saves.delete(row.uuid);
    }
  }

  /** W48: the review's first load (set_review_opened), and inspection finishing while it is open */
  private trackSetLoaded(before: DrawingIssueSet | null): void {
    const s = this.set;
    if (!s) {
      return;
    }
    const pages = s.revisions?.length ?? 0;
    if (!before) {
      this.openedAt = Date.now();
      this.track.track('set_review_opened', {
        set_uuid: this.setUuid, pages, needs_attention: this.attentionCount, skipped: pages - this.publishRows.length, status: s.status,
      });
    } else if (this.isInspecting(before) && !this.isInspecting(s)) {
      const failed = s.inspect?.status === 'failed';
      this.track.track('set_inspect_finished', {
        set_uuid: this.setUuid, pages, outcome: failed ? 'failed' : 'ready', needs_attention: this.attentionCount,
        error_code: failed ? s.inspect?.error || 'UNKNOWN' : undefined,
      });
    }
  }

  private fail(e: HttpErrorResponse): void {
    this.track.error('set_review', e);
    this.notify.notification(this.api.requestFailure(e), 'error');
  }
}
